import {createLucideIcon, type IconNode} from 'lucide-react';

/*
 * Icons Lucide doesn't have, drawn to Lucide's conventions (24x24 grid,
 * 2px round strokes, no fill) so they sit alongside the real ones. Each is
 * a Lucide icon component that can be passed to <Icon>, and the raw nodes
 * are exported too for the whiteboard canvas (see canvas/resourceCardIcons).
 *
 * The brand logos at the end are the exception: Lucide deliberately has no
 * brand icons, so those are the filled Simple Icons shapes (CC0,
 * simpleicons.org, v16.33.0).
 */

const filled = {fill: 'currentColor', stroke: 'none'};

// The outline shared by the file-type icons, from Lucide's `file`.
const fileOutline: IconNode = [
    [
        'path',
        {
            d: 'M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z'
        }
    ],
    ['path', {d: 'M14 2v5a1 1 0 0 0 1 1h5'}]
];

export const customIconNodes = {
    caretDown: [['path', {d: 'M6 9h12l-6 7z', fill: 'currentColor'}]],
    chalkboard: [
        ['path', {d: 'M4 17V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12'}],
        ['path', {d: 'M2 17h20'}],
        ['path', {d: 'M7 8h6'}],
        ['path', {d: 'M7 12h3'}],
        ['path', {d: 'm6 17-1.5 4'}],
        ['path', {d: 'm18 17 1.5 4'}]
    ],
    chessBoard: [
        ['rect', {x: '3', y: '3', width: '18', height: '18', rx: '2'}],
        [
            'path',
            {d: 'M9 3h6v6H9zM3 9h6v6H3zM15 9h6v6h-6zM9 15h6v6H9z', ...filled}
        ]
    ],
    drawPolygon: [
        ['path', {d: 'M5 5 19 7l-2 12-11-3z'}],
        ['circle', {cx: '5', cy: '5', r: '2.25', ...filled}],
        ['circle', {cx: '19', cy: '7', r: '2.25', ...filled}],
        ['circle', {cx: '17', cy: '19', r: '2.25', ...filled}],
        ['circle', {cx: '6', cy: '16', r: '2.25', ...filled}]
    ],
    filePdf: [
        ...fileOutline,
        ['path', {d: 'M7 18v-5h1.25a1.5 1.5 0 0 1 0 3H7', strokeWidth: '1.5'}],
        ['path', {d: 'M11 13v5h.75a2.5 2.5 0 0 0 0-5z', strokeWidth: '1.5'}],
        ['path', {d: 'M15.5 18v-5H18', strokeWidth: '1.5'}],
        ['path', {d: 'M15.5 15.5h2', strokeWidth: '1.5'}]
    ],
    fileWord: [
        ...fileOutline,
        ['path', {d: 'm7.5 12 1.5 6 3-4.5 3 4.5 1.5-6'}]
    ],
    filePowerpoint: [
        ...fileOutline,
        ['path', {d: 'M10 19v-7h2.5a2.5 2.5 0 0 1 0 5H10'}]
    ],
    hourglassHalf: [
        ['path', {d: 'M5 22h14'}],
        ['path', {d: 'M5 2h14'}],
        [
            'path',
            {
                d: 'M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22'
            }
        ],
        [
            'path',
            {
                d: 'M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2'
            }
        ],
        ['path', {d: 'M9.5 7.5h5L12 10z', ...filled}],
        ['path', {d: 'M8.5 20h7L12 16.5z', ...filled}]
    ],
    laptopCode: [
        [
            'path',
            {
                d: 'M18 5a2 2 0 0 1 2 2v8.526a2 2 0 0 0 .212.897l1.068 2.127a1 1 0 0 1-.9 1.45H3.62a1 1 0 0 1-.9-1.45l1.068-2.127A2 2 0 0 0 4 15.526V7a2 2 0 0 1 2-2z'
            }
        ],
        ['path', {d: 'M20.054 15.987H3.946'}],
        ['path', {d: 'm10 8.5-2 2 2 2'}],
        ['path', {d: 'm14 8.5 2 2-2 2'}]
    ],
    networkWired: [
        ['rect', {x: '9', y: '2', width: '6', height: '5', rx: '1'}],
        ['path', {d: 'M12 7v4'}],
        ['path', {d: 'M4 16v-4a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v4'}],
        ['path', {d: 'M12 11v5'}],
        ['rect', {x: '2', y: '16', width: '4', height: '5', rx: '1'}],
        ['rect', {x: '10', y: '16', width: '4', height: '5', rx: '1'}],
        ['rect', {x: '18', y: '16', width: '4', height: '5', rx: '1'}]
    ],
    ring: [
        ['circle', {cx: '12', cy: '15', r: '6'}],
        ['path', {d: 'M9.5 5.5 12 9l2.5-3.5-1-1.5h-3z'}]
    ],
    running: [
        ['circle', {cx: '15', cy: '4', r: '2'}],
        ['path', {d: 'm13.5 8-2.5 6'}],
        ['path', {d: 'M13.5 8 9 9.5l-2 3'}],
        ['path', {d: 'm13.5 8 2.5 3.5 3.5-1'}],
        ['path', {d: 'm11 14 4 2.5-1 4.5'}],
        ['path', {d: 'm11 14-2 4-4 .5'}]
    ],
    screwdriverWrench: [
        [
            'path',
            {
                d: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.106-3.105c.32-.322.863-.22.983.218a6 6 0 0 1-8.259 7.057l-7.91 7.91a1 1 0 0 1-2.999-3l7.91-7.91a6 6 0 0 1 7.057-8.259c.438.12.54.662.219.984z'
            }
        ],
        ['path', {d: 'M2.5 2.5 4 6l1.5.5L10 11'}],
        ['path', {d: 'm2.5 2.5 3.5 1.5.5 1.5'}],
        ['path', {d: 'm14 14 6.5 6.5a1.4 1.4 0 0 1-2 2L12 16'}]
    ],
    shareSquare: [
        ['rect', {x: '3', y: '3', width: '18', height: '18', rx: '2'}],
        ['circle', {cx: '15', cy: '8', r: '1.75'}],
        ['circle', {cx: '9', cy: '12', r: '1.75'}],
        ['circle', {cx: '15', cy: '16', r: '1.75'}],
        ['path', {d: 'm10.46 12.97 3.08 2.06'}],
        ['path', {d: 'm10.46 11.03 3.08-2.06'}]
    ],
    sink: [
        ['path', {d: 'M3 13h18v1a6 6 0 0 1-6 6H9a6 6 0 0 1-6-6z'}],
        ['path', {d: 'M10 13V6.5a3.5 3.5 0 0 1 7 0V8'}],
        ['path', {d: 'M6 10h4'}]
    ],
    smileWink: [
        ['circle', {cx: '12', cy: '12', r: '10'}],
        ['path', {d: 'M16.472 15a6 6 0 01-8.943 0'}],
        ['path', {d: 'M9 10V9'}],
        ['path', {d: 'M14 10c.5-1 2-1 2.5 0'}]
    ],
    surprise: [
        ['circle', {cx: '12', cy: '12', r: '10'}],
        ['path', {d: 'M9 10V9'}],
        ['path', {d: 'M15 10V9'}],
        ['circle', {cx: '12', cy: '15.5', r: '2'}]
    ],
    userClock: [
        ['path', {d: 'M10 15H6a4 4 0 0 0-4 4v2'}],
        ['circle', {cx: '9', cy: '7', r: '4'}],
        ['circle', {cx: '17', cy: '17', r: '5'}],
        ['path', {d: 'M17 15v2l1.5 1'}]
    ],
    userGraduate: [
        ['path', {d: 'M12 2 5 5l7 3 7-3z'}],
        ['path', {d: 'M19 5v3.5'}],
        ['path', {d: 'M8.5 7.5V9a3.5 3.5 0 0 0 7 0V7.5'}],
        ['path', {d: 'M19 21v-1a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v1'}]
    ],
    usersCog: [
        ['path', {d: 'M10 15H6a4 4 0 0 0-4 4v2'}],
        ['path', {d: 'M16 3.128a4 4 0 0 1 0 7.744'}],
        ['path', {d: 'm14.305 16.53.923-.382'}],
        ['path', {d: 'm15.228 13.852-.923-.383'}],
        ['path', {d: 'm16.852 12.228-.383-.923'}],
        ['path', {d: 'm16.852 17.772-.383.924'}],
        ['path', {d: 'm19.148 12.228.383-.923'}],
        ['path', {d: 'm19.53 18.696-.382-.924'}],
        ['path', {d: 'm20.772 13.852.924-.383'}],
        ['path', {d: 'm20.772 16.148.924.383'}],
        ['circle', {cx: '18', cy: '15', r: '3'}],
        ['circle', {cx: '9', cy: '7', r: '4'}]
    ],
    waveSquare: [['path', {d: 'M2 17V7h5v10h5V7h5v10h5'}]],

    youtubeBrand: [
        [
            'path',
            {
                d: 'M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z',
                ...filled
            }
        ]
    ],
    dropboxBrand: [
        [
            'path',
            {
                d: 'M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452l-6 3.822zM18 9.452l-6 3.822 6 3.822 6-3.822-6-3.822zM6 18.371l6.001 3.822 6-3.822-6-3.822L6 18.371z',
                ...filled
            }
        ]
    ],
    googleDriveBrand: [
        [
            'path',
            {
                d: 'M12.01 1.485c-2.082 0-3.754.02-3.743.047.01.02 1.708 3.001 3.774 6.62l3.76 6.574h3.76c2.081 0 3.753-.02 3.742-.047-.005-.02-1.708-3.001-3.775-6.62l-3.76-6.574zm-4.76 1.73a789.828 789.861 0 0 0-3.63 6.319L0 15.868l1.89 3.298 1.885 3.297 3.62-6.335 3.618-6.33-1.88-3.287C8.1 4.704 7.255 3.22 7.25 3.214zm2.259 12.653-.203.348c-.114.198-.96 1.672-1.88 3.287a423.93 423.948 0 0 1-1.698 2.97c-.01.026 3.24.042 7.222.042h7.244l1.796-3.157c.992-1.734 1.85-3.23 1.906-3.323l.104-.167h-7.249z',
                ...filled
            }
        ]
    ]
} satisfies Record<string, IconNode>;

// React needs a key on each element, as Lucide's own icon data has.
const create = (name: string, node: IconNode) =>
    createLucideIcon(
        name,
        node.map(([tag, attrs], i) => [tag, {...attrs, key: `${tag}-${i}`}])
    );

const n = customIconNodes;

export const CaretDownIcon = create('caret-down', n.caretDown);
export const ChalkboardIcon = create('chalkboard', n.chalkboard);
export const ChessBoardIcon = create('chess-board', n.chessBoard);
export const DrawPolygonIcon = create('draw-polygon', n.drawPolygon);
export const FilePdfIcon = create('file-pdf', n.filePdf);
export const FileWordIcon = create('file-word', n.fileWord);
export const FilePowerpointIcon = create('file-powerpoint', n.filePowerpoint);
export const HourglassHalfIcon = create('hourglass-half', n.hourglassHalf);
export const LaptopCodeIcon = create('laptop-code', n.laptopCode);
export const NetworkWiredIcon = create('network-wired', n.networkWired);
export const RingIcon = create('ring', n.ring);
export const RunningIcon = create('running', n.running);
export const ScrewdriverWrenchIcon = create(
    'screwdriver-wrench',
    n.screwdriverWrench
);
export const ShareSquareIcon = create('share-square', n.shareSquare);
export const SinkIcon = create('sink', n.sink);
export const SmileWinkIcon = create('smile-wink', n.smileWink);
export const SurpriseIcon = create('surprise', n.surprise);
export const UserClockIcon = create('user-clock', n.userClock);
export const UserGraduateIcon = create('user-graduate', n.userGraduate);
export const UsersCogIcon = create('users-cog', n.usersCog);
export const WaveSquareIcon = create('wave-square', n.waveSquare);

export const YoutubeBrandIcon = create('youtube-brand', n.youtubeBrand);
export const DropboxBrandIcon = create('dropbox-brand', n.dropboxBrand);
export const GoogleDriveBrandIcon = create(
    'google-drive-brand',
    n.googleDriveBrand
);
